/**
 * 📄
 * Model representing a single line in the trade history table
 */
export default class TradeModel {
  public signalStrength: number;
  public pPctTraded: number;
  public pRatio: string;

  constructor(
    public botId: string,
    public botName: string,
    public type: string,
    public date: number,
    public price: number,
    public market: string,
    public amount: number,
    public balance: number,
    public fee: number,
    public fee_asset: string,
    public change: number | undefined,
    public obt_fee: number,
    public cryptoBefore: number,
    public stableBefore: number,
    public cryptoAfter: number,
    public stableAfter: number,
    public order_type?: TradeOrderType
  ) {
    this.signalStrength =
      (this.type === OrderSide.BUY.toString()
        ? (this.amount * this.price) / this.stableBefore
        : this.amount / this.cryptoBefore) * 100;

    if (this.signalStrength > 100) {
      this.signalStrength = 100;
    }

    this.pPctTraded =
      ((this.amount * this.price) /
        (this.stableBefore + this.cryptoBefore * this.price)) *
      100;

    this.pRatio = this.getPortfolioDistribution();
  }

  // 🚩 Flag for whether the current trade type is limit
  public get isLimitOrder(): boolean {
    return this.order_type == TradeOrderType.LimitOrder;
  }

  /**
   * 🏗 Calculates the distribution of the bot's portfolio after that specific trade
   */
  public getPortfolioDistribution(): string {
    const totalBalance = this.cryptoAfter * this.price + this.stableAfter;
    let cryptoDistribution =
      ((this.cryptoAfter * this.price) / totalBalance) * 100;
    let stablecoinDistribution = (this.stableAfter / totalBalance) * 100;

    return (
      this.applyValueBoundaries(cryptoDistribution).toFixed(0) +
      "%/" +
      this.applyValueBoundaries(stablecoinDistribution).toFixed(0) +
      "%"
    );
  }

  /**
   * 🔨 Formats a given number to a number between 0 and 100
   * @param value
   */
  private applyValueBoundaries(value: number) {
    return value < 0 ? 0 : value > 100 ? 100 : value;
  }

  public static transformTradeHistory(trades: any[], startingBalance: number): TradeModel[] {
    const formattedTrades: TradeModel[] = [];
    var portfolioBalanceCrypto = 0;
    var portfolioBalanceStablecoin = startingBalance;

    trades.forEach((trade: any) => {
      const portfolioBalanceCryptoAfterTrade =
        trade.type.toUpperCase() == OrderSide.BUY
          ? portfolioBalanceCrypto + trade.amount
          : portfolioBalanceCrypto - trade.amount;

      const portfolioBalanceStablecoinAfterTrade =
        trade.type.toUpperCase() == OrderSide.BUY
          ? portfolioBalanceStablecoin - trade.amount * trade.price
          : portfolioBalanceStablecoin + trade.amount * trade.price;

      formattedTrades.push(
        new TradeModel(
          trade.bot_id,
          trade.strategy_name,
          trade.type.toUpperCase(),
          trade.date,
          trade.price,
          trade.market,
          trade.amount,
          trade.balance ? Number(trade.balance.toFixed(2)) : 0,
          trade.fee ? Number(trade.fee.toFixed(6)) : 0,
          trade.fee_asset,
          trade.change ? Number((trade.change * 100).toFixed(2)) : undefined,
          trade.OBT_fee ? Number(trade.OBT_fee.toFixed(6)) : 0,
          portfolioBalanceCrypto,
          portfolioBalanceStablecoin,
          portfolioBalanceCryptoAfterTrade,
          portfolioBalanceStablecoinAfterTrade,
          trade.order_type
        )
      );

      portfolioBalanceCrypto = portfolioBalanceCryptoAfterTrade;
      portfolioBalanceStablecoin = portfolioBalanceStablecoinAfterTrade;
    });
    return formattedTrades;
  }

  /**
   * USED FOR TESTING ONLY
   * Generates a random trade log of 10 to 20 trades
   */
  private static generateTradeLog(): TradeModel[] {
    let tradeLog: TradeModel[] = [];

    for (let i = 0; i < Math.random() * 10 + 10; i++) {
      let type = i % 2 == 0 ? "sell" : "buy";
      tradeLog.push(
        new TradeModel(
          "Some Bot Id",
          "MA Crossover",
          type,
          Math.floor(Math.random() * (1600353061 - 1600243000)) + 1600243000,
          Math.random() * 10000 + 9264.0,
          "BTC:USDT",
          (Math.random() * 20 + 5) / 100,
          (Math.random() * 20 + 5) / 100,
          i * 10,
          "USDT",
          Math.floor(Math.random() * -0.001) + 0.005,
          0,
          0,
          0,
          0,
          0,
          TradeOrderType.LimitOrder
        )
      );
    }
    return tradeLog;
  }
}

export enum OrderSide {
  BUY = "BUY",
  SELL = "SELL",
}

export enum TradeOrderType {
  MarketOrder = "MARKET",
  LimitOrder = "LIMIT",
}
